<template>
  <div class="billing-detail-page">
    <page-breadcrumb
      :category="$i18nPresenter.t('account')"
      :text="breadCrumbText"
      :subText="breadCumbSubtext"
      @click="goBackToBillingList()"
      :title="breadCrumbTitle"
    />
    <div class="px-4 py-6 bg-base-0">
      <div
        class="flex justify-center"
        v-if="isLoadingData"
      >
        <aha-spinner />
      </div>
      <div
        class="flex flex-col flex-wrap"
        id="print-area"
        v-if="!isLoadingData && invoice.invoiceId"
      >
        <div class="pl-4 pr-4 mb-4">
          <a href="https://ahaslides.com" class="logo">
            <aha-logo
              class="navbar-brand-logo-img"
              width="140"
              height="32"
            />
          </a>
          <address class="my-4 not-italic">
            <h3 class="text-2xl">
              <strong>AhaSlides Pte Ltd</strong>
            </h3>
            <p>
              20A Tanjong Pagar Road
              <br> Singapore 088443
            </p>
          </address>
        </div>

        <div class="px-4 my-4">
          <address class="not-italic">
            <span class="font-bold uppercase text-base-80">{{ $i18nPresenter.t('Billed to:') }}</span>
            <h3 class="mb-0 text-2xl">
              <strong>
                {{ `${$store.state.currentUser.firstName} ${$store.state.currentUser.lastName}` }}
              </strong>
            </h3>
            {{ $store.state.currentUser.email }}
            <br>
            <PreLine :text="$store.state.currentUser.address" />
          </address>
        </div>

        <div class="px-4 my-4">
          {{ $i18nPresenter.t('Invoice no.:') }} {{ invoice.invoiceNumber }}
          <br>{{ $i18nPresenter.t('Date:') }} {{ invoice.paymentDate | dateBill }}
          <br>{{ $i18nPresenter.t('Status:') }} <span class="uppercase">{{ invoice.status }}</span>
          <div v-if="invoice && invoice.paymentMethod">
            <span>{{ $i18nPresenter.t('Payment method:') }}</span>
            <PaymentMethod :source="invoice.source" :payment-method="invoice.paymentMethod" />
          </div>
        </div>

        <div class="pl-4 pr-4 my-4">
          <div class="block w-full overflow-auto scrolling-touch" style="clear: both;">
            <table class="w-full max-w-full bg-transparent border-collapse table-hover">
              <thead>
                <tr>
                  <th class="py-2 text-left text-gray-700 uppercase">
                    {{ $i18nPresenter.t('Item') }}
                  </th>
                  <th class="py-2 text-right text-gray-700 uppercase">
                    {{ $i18nPresenter.t('Price') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  :key="index"
                  v-for="(item, index) in invoice.lines"
                >
                  <td class="py-2 border-t border-b border-base-30">
                    {{ item.description }}
                  </td>
                  <td class="py-2 text-right border-t border-b border-base-30">
                    <span>{{ roundNumber(item.amount) }} </span>
                    <span class="uppercase">{{ invoice.currency }}</span>
                  </td>
                </tr>
                <tr v-if="discount > 0">
                  <td class="py-2 border-t border-b border-base-30">
                    {{ $i18nPresenter.t('Discount') }}
                  </td>
                  <td class="py-2 text-right border-t border-b border-base-30">
                    <span>-{{ discount }} </span>
                    <span class="uppercase">{{ invoice.currency }}</span>
                  </td>
                </tr>
                <tr v-if="invoice.amount - invoice.amountPaid > 0">
                  <td class="py-2 border-t border-b border-base-30">
                    {{ $i18nPresenter.t('Credit') }}
                  </td>
                  <td class="py-2 text-right border-t border-b border-base-30">
                    <span>-{{ roundNumber(invoice.amount - invoice.amountPaid) }} </span>
                    <span class="uppercase">{{ invoice.currency }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="px-4">
          <div class="text-right pull-right">
            <h3 class="text-xl">
              <b>{{ $i18nPresenter.t('Total:') }} &nbsp;</b>
              <strong>{{ totalAmountPaid() }}
                <span class="uppercase"> {{ invoice.currency }}</span>
              </strong>
            </h3>
          </div>
          <div class="clearfix" />
        </div>
      </div>
    </div>
    <div class="flex items-center my-5" v-if="!isLoadingData && invoice.invoiceId">
      <div class="px-4">
        <aha-button
          id="print"
          @click="print"
          class="mr-2"
        >
          {{ $i18nPresenter.t('Print') }}
        </aha-button>
        <aha-button variant="base">
          <router-link to="/apps/billing-and-payments">
            {{ $i18nPresenter.t('Go back') }}
          </router-link>
        </aha-button>
      </div>
    </div>
  </div>
</template>

<script>
import Config from '@/config'
import Util from '@/util'
import PreLine from '@/components/UI/PreLine'

import PaymentMethod from '@/components-v2/billing/PaymentMethod.vue'
import PageBreadcrumb from '../common/page-breadcrumb/PageBreadcrumb.vue'

export default {
  components: {
    PreLine,
    PageBreadcrumb,
    PaymentMethod,
  },
  data() {
    return {
      invoice: {
        paymentMethod: {},
        lines: [],
      },
      user: {},
      discount: 0,
      isLoadingData: false,
    }
  },
  computed: {
    breadCrumbTitle() {
      return this.$i18nPresenter.t('Billing & Payments')
    },
    breadCrumbText() {
      return this.$i18nPresenter.t('Billing & Payments')
    },
    breadCumbSubtext() {
      return this.invoice.invoiceNumber ? `${this.$i18nPresenter.t('Invoice')} ${this.invoice.invoiceNumber}` : ''
    },
  },
  mounted() {
    this.loadInvoice()
    document.body.classList.add('printable')
  },
  destroyed() {
    document.body.classList.remove('printable')
  },
  methods: {
    roundNumber: Util.roundNumber,
    goBackToBillingList() {
      this.$router.push({ name: 'BillingList' })
    },
    print() {
      window.print()
    },
    totalAmountPaid() {
      if (this.invoice.currency === 'eur') {
        return this.roundNumber(this.invoice.amountPaidRaw)
      }
      return this.roundNumber(this.invoice.amountPaid)
    },
    loadInvoice() {
      this.isLoadingData = true
      this.$http.get(Config.api.invoice + this.$route.params.id)
        .then(
          (res) => {
            this.invoice = res.body.invoice
            // Hotfix edu plan name Typo
            this.invoice.lines = this.invoice.lines.map((item) => {
              if (item.description === 'Edu Small Yeaerly') {
                item.description = 'Edu Yearly Small'
              }
              if (this.invoice.currency === 'eur') {
                // convert usd to eur
                item.amount = item.amountRaw
                item.discountAmount = item.discountAmountRaw
              }
              return item
            })
            this.discount = Util.roundNumber(this.invoice.lines
              .map((item) => item.discountAmount || 0)
              .reduce((a, b) => parseFloat(a) + parseFloat(b), 0))
            this.isLoadingData = false
          },
          () => {
            this.isLoadingData = false
            this.$router.push({ name: 'BillingList' })
          },
        )
    },
  },
}
</script>

<style lang="scss">
.billing-detail-page {
  @apply p-4 md:px-10 md:py-6;
}

@media print {
  header,footer { /** Hide the header & footer in print preview */
    display: none;
  }

  * {
    visibility: hidden;
  }
  #billing-page-breadcrumb {
    display: none;
  }
  #print-area * {
    visibility: visible;
  }
}
</style>
